
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
    FaSearch, FaSync, FaEye, FaDownload, FaUser, FaBriefcase, FaBuilding, FaCalendarAlt, FaCheck, FaTimes, FaSpinner, FaFilter
} from 'react-icons/fa';
import { FiBriefcase, FiSearch, FiClipboard, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import "./EmployeeDashboard.css";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';
const API_DOMAIN = 'https://ingrainhirebackend.ingrainsystems.com';

const StatusBadge = ({ status, className = "" }) => {
  const badges = {
    signed: "bg-emerald-50 text-emerald-700 border-emerald-100 shadow-sm",
    pending: "bg-amber-50 text-amber-700 border-amber-100 shadow-sm",
    missing: "bg-gray-50 text-gray-400 border-gray-100 shadow-sm"
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all duration-300 ${badges[status] || badges.missing} ${className}`}>
      {status === 'signed' && <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>}
      {status === 'pending' && <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1.5 shadow-[0_0_8px_rgba(245,158,11,0.5)]"></div>}
      {status === 'missing' && <div className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-1.5"></div>}
      {status}
    </span>
  );
};

const AcceptanceLetter = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState("");
    const [deptFilter, setDeptFilter] = useState("");
    const [dateFilter, setDateFilter] = useState("");
    
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    
    // Global filter states
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [roleSearchQuery, setRoleSearchQuery] = useState("");
    const roleDropdownRef = useRef(null);

    const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);
    const [deptSearchQuery, setDeptSearchQuery] = useState("");
    const deptDropdownRef = useRef(null);

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
    });

    // Get clientId from localStorage
    const getClientId = () => {
        return localStorage.getItem('clientId') || localStorage.getItem('clientCustomId') || '';
    };

    useEffect(() => {
        fetchApplications();
        fetchRoles();
        fetchDepartments();

        const handleClickOutside = (event) => {
            if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
                setIsRoleDropdownOpen(false);
            }
            if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target)) {
                setIsDeptDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchRoles = async () => {
        try {
            const clientId = getClientId();
            const res = await axios.get(`${API_BASE_URL}/roles/all?clientId=${clientId}`);
            if (res.data.success) {
                setRoles(res.data.data);
            }
        } catch (err) {
            console.error("Failed to fetch roles:", err);
        }
    };

    const fetchDepartments = async () => {
        try {
            const clientId = getClientId();
            const res = await axios.get(`${API_BASE_URL}/employees/get-employees/${clientId}`);
            if (Array.isArray(res.data)) {
                const uniqueDepartments = [...new Set(res.data.map(emp => emp.department).filter(Boolean))];
                setDepartments(uniqueDepartments.sort());
            } else if (res.data.employees) {
                const uniqueDepartments = [...new Set(res.data.employees.map(emp => emp.department).filter(Boolean))];
                setDepartments(uniqueDepartments.sort());
            } else if (res.data.data && Array.isArray(res.data.data)) {
                const uniqueDepartments = [...new Set(res.data.data.map(emp => emp.department).filter(Boolean))];
                setDepartments(uniqueDepartments.sort());
            }
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    };

    const fetchApplications = async () => {
        try {
            setLoading(true);
            const clientId = getClientId();
            const res = await axios.get(`${API_BASE_URL}/applications/documents/${clientId}`);
            if (res.data.success) {
                setApplications(res.data.data || []);
            } else {
                setApplications([]);
            }
        } catch (error) {
            console.error("Error fetching documents:", error);
            toast.error("Failed to load documents");
            setApplications([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDocUrl = (path) => {
        if (!path) return "";
        if (path.startsWith('http')) {
            return path;
        }
        const cleanPath = path.replace(/\\/g, "/");
        return `${API_DOMAIN}/${cleanPath}`;
    };

    function getSignedDocs(app) {
        const docs = [];
        if (app.candidateAgreementsUpload && String(app.candidateAgreementsUpload).trim().length > 0) {
            docs.push({ type: "Agreement (Legacy)", path: app.candidateAgreementsUpload, date: app.updatedAt, id: `${app._id}-legacy` });
        }
        if (app.documentHistory) {
            app.documentHistory.forEach((hist, idx) => {
                if (hist.signedPath && String(hist.signedPath).trim().length > 0) {
                    docs.push({ type: hist.documentType || "Document", path: hist.signedPath, date: hist.signedAt || hist.sentAt, id: `${app._id}-hist-${idx}` });
                }
            });
        }
        return docs;
    }

    const filteredApps = applications.filter(app => {
        const query = searchTerm.toLowerCase();
        const candidateName = `${app.firstName} ${app.lastName}`.toLowerCase();
        const candEmail = (app.candidateId?.email || "").toLowerCase();
        const role = (app.jobId?.role || "").toLowerCase();
        const dept = (app.jobId?.department || "").toLowerCase();
        
        const hasSignedDocs = getSignedDocs(app).length > 0;
        const isProcessActive = app.offerSentAt || (app.documentHistory && app.documentHistory.length > 0);
        
        const matchesSearch = candidateName.includes(query) || role.includes(query) || candEmail.includes(query);
        const matchesRole = roleFilter ? role === roleFilter.toLowerCase() : true;
        const matchesDept = deptFilter ? dept === deptFilter.toLowerCase() : true;
        const matchesDate = dateFilter ? new Date(app.updatedAt).toISOString().slice(0, 10) === dateFilter : true;

        return (hasSignedDocs || isProcessActive) && matchesSearch && matchesRole && matchesDept && matchesDate;
    });

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm("");
        setRoleFilter("");
        setDeptFilter("");
        setDateFilter("");
        setRoleSearchQuery("");
        setDeptSearchQuery("");
    };

    // Pagination Handlers
    const handleItemsPerPageChange = (limit) => {
        setPagination({
            currentPage: 1,
            limit: limit,
            totalCount: filteredApps.length,
            totalPages: Math.ceil(filteredApps.length / limit)
        });
    };

    const handlePrevPage = () => {
        if (pagination.currentPage > 1) {
            setPagination(prev => ({
                ...prev,
                currentPage: prev.currentPage - 1
            }));
        }
    };

    const handleNextPage = () => {
        if (pagination.currentPage < pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: prev.currentPage + 1
            }));
        }
    };

    const handlePageClick = (page) => {
        setPagination(prev => ({
            ...prev,
            currentPage: page
        }));
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        for (let i = 1; i <= pagination.totalPages; i++) {
            if (
                i === 1 ||
                i === pagination.totalPages ||
                (i >= pagination.currentPage - 2 && i <= pagination.currentPage + 2)
            ) {
                pageNumbers.push(i);
            } else if (i === pagination.currentPage - 3 || i === pagination.currentPage + 3) {
                pageNumbers.push("...");
            }
        }
        return pageNumbers;
    };

    // Update pagination when filtered results change
    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            totalCount: filteredApps.length,
            totalPages: Math.ceil(filteredApps.length / prev.limit),
            currentPage: 1
        }));
    }, [filteredApps.length, searchTerm, roleFilter, deptFilter, dateFilter]);

    // Calculate pagination
    const indexOfLastItem = pagination.currentPage * pagination.limit;
    const indexOfFirstItem = indexOfLastItem - pagination.limit;
    const currentItems = filteredApps.slice(indexOfFirstItem, indexOfLastItem);

    if (loading) return (
        <div className="w-full min-h-screen p-2 flex items-center justify-center">
            <div className="text-center">
                <div className="w-10 h-10 border-4 border-indigo-50 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading acceptance letters...</p>
            </div>
        </div>
    );

    return (
        <div style={{backgroundColor:'white'}} className="emp-dash">
            <main className="p-4 sm:p-6 lg:p-8">
                {/* Dashboard Header */}
                <div className="emp-dash__header">
                    <div>
                        <h1 className="emp-dash__greeting">
                            Acceptance <span>Letters</span>
                        </h1>
                        <p className="emp-dash__subtitle">
                            Manage and review all signed documents
                        </p>
                    </div>
                    <div className="emp-dash__date-pill">
                        <FiFileText />
                        <span>
                            {new Date().toLocaleDateString("en-US", {
                                weekday: "short",
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                            })}
                        </span>
                    </div>
                </div>

                {/* Filters Section */}
                <div className="emp-dash__card mb-6">
                    <div className="emp-dash__card-header">
                        <div>
                            <h3 className="emp-dash__card-title flex items-center gap-2">
                                <FiSearch className="text-blue-600" /> Filter Documents
                            </h3>
                            <p className="emp-dash__card-desc">Search by name, role, department, or date</p>
                        </div>
                    </div>
                    <div className="emp-dash__card-body bg-gray-50/50">
                        <div className="flex flex-wrap items-center gap-2">

                            {/* Search Bar */}
                            <div className="relative flex-1 min-w-[180px]">
                                <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                                <input
                                    type="text"
                                    placeholder="Search name, role..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Role Filter Button */}
                            <div className="relative" ref={roleDropdownRef}>
                                <button
                                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                                    className={`h-8 px-3 text-xs font-medium rounded-md transition flex items-center gap-1 ${
                                        roleFilter 
                                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                                    }`}
                                >
                                    <FiBriefcase className="text-xs" /> Role {roleFilter && `: ${roleFilter}`}
                                </button>
                        
                            {/* Role Filter Dropdown */}
                            {isRoleDropdownOpen && (
                                <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    <div className="p-2 border-b border-gray-100 bg-gray-50">
                                        <div className="relative">
                                            <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                                            <input
                                                type="text"
                                                className="w-full py-1 pl-7 pr-2 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                placeholder="Search roles..."
                                                value={roleSearchQuery}
                                                onChange={(e) => setRoleSearchQuery(e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                            />
                                        </div>
                                    </div>
                                    <div 
                                        onClick={() => {
                                            setRoleFilter('');
                                            setIsRoleDropdownOpen(false);
                                            setRoleSearchQuery('');
                                        }}
                                        className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium ${
                                            !roleFilter ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                        }`}
                                    >
                                        All Roles
                                    </div>
                                    {roles
                                        .filter(r => (r.name || "").toLowerCase().includes(roleSearchQuery.toLowerCase()))
                                        .map((r) => (
                                            <div 
                                                key={r._id}
                                                onClick={() => {
                                                    setRoleFilter(r.name);
                                                    setIsRoleDropdownOpen(false);
                                                    setRoleSearchQuery('');
                                                }}
                                                className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${
                                                    roleFilter === r.name ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                                                }`}
                                            >
                                                {r.name}
                                            </div>
                                        ))}
                                    {roles.filter(r => (r.name || "").toLowerCase().includes(roleSearchQuery.toLowerCase())).length === 0 && (
                                        <div className="px-3 py-2 text-xs text-gray-400 text-center">
                                            No roles found
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                            {/* Department Filter - Searchable Dropdown */}
                            <div className="relative" ref={deptDropdownRef}>
                                <button
                                    onClick={() => setIsDeptDropdownOpen(!isDeptDropdownOpen)}
                                    className={`h-8 px-3 text-xs font-medium rounded-md transition flex items-center gap-1 ${
                                        deptFilter 
                                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                                    }`}
                                >
                                    <FiClipboard className="text-xs" /> Dept {deptFilter && `: ${deptFilter}`}
                                </button>
                                
                                {isDeptDropdownOpen && (
                                    <div className="absolute z-50 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        <div className="p-2 border-b border-gray-100 bg-gray-50">
                                            <div className="relative">
                                                <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                                                <input
                                                    type="text"
                                                    className="w-full py-1 pl-7 pr-2 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    placeholder="Search departments..."
                                                    value={deptSearchQuery}
                                                    onChange={(e) => setDeptSearchQuery(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    autoFocus
                                                />
                                            </div>
                                        </div>
                                        <div 
                                            onClick={() => {
                                                setDeptFilter('');
                                                setIsDeptDropdownOpen(false);
                                                setDeptSearchQuery('');
                                            }}
                                            className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium ${
                                                !deptFilter ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                            }`}
                                        >
                                            All Depts
                                        </div>
                                        {departments
                                            .filter(d => (d || "").toLowerCase().includes(deptSearchQuery.toLowerCase()))
                                            .map((dept, idx) => (
                                                <div 
                                                    key={idx}
                                                    onClick={() => {
                                                        setDeptFilter(dept);
                                                        setIsDeptDropdownOpen(false);
                                                        setDeptSearchQuery('');
                                                    }}
                                                    className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${
                                                        deptFilter === dept ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                                                    }`}
                                                >
                                                    {dept}
                                                </div>
                                            ))}
                                        {departments.filter(d => (d || "").toLowerCase().includes(deptSearchQuery.toLowerCase())).length === 0 && (
                                            <div className="px-3 py-2 text-xs text-gray-400 text-center">
                                                No departments found
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Date Filter */}
                            <div className="relative w-[130px]">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none">
                                    Date:
                                </span>
                                <input
                                    type="date"
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    className="w-full pl-12 pr-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Clear Filters Button */}
                            {(searchTerm || roleFilter || deptFilter || dateFilter) && (
                                <button
                                    onClick={clearFilters}
                                    className="h-8 px-3 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div className="emp-dash__card mb-6">
                    {loading ? (
                        <div className="py-10 text-center">
                            <div className="flex flex-col items-center justify-center gap-3">
                                <div className="emp-dash__spinner"></div>
                                <span className="text-sm font-medium text-gray-500">Syncing documents...</span>
                            </div>
                        </div>
                    ) : filteredApps.length > 0 ? (
                        <div className="emp-dash__table-wrap">
                            <table className="emp-dash__table">
                                <thead>
                                    <tr>
                                        <th className="py-2 text-center">Candidate Details</th>
                                        <th className="py-2 text-center">Role</th>
                                        <th className="py-2 text-center">Offer Letter</th>
                                        <th className="py-2 text-center">Signed Agreements</th>
                                        <th className="py-2 text-center">Status</th>
                                        <th className="py-2 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.map((app) => {
                                        const docs = getSignedDocs(app);
                                        const offerDoc = docs.find(d => d.type === "Offer" || d.type === "Offer Letter" || d.type.toLowerCase().includes("offer"));
                                        const agreementDocs = docs.filter(d => d.type.toLowerCase().includes("agreement"));
                                        
                                        const candName = `${app.firstName} ${app.lastName}`;
                                        
                                        return (
                                            <tr key={app._id} className="border-b hover:bg-gray-50 transition-colors">
                                                <td className="px-2 py-2 font-medium text-center">
                                                    <div className="text-gray-900 whitespace-nowrap">{candName}</div>
                                                    <div className="text-[10px] text-gray-600">{new Date(app.updatedAt).toLocaleDateString()}</div>
                                                </td>
                                                <td className="px-2 py-2 text-center">
                                                    <span className="inline-block px-2 py-1 bg-blue-50 text-blue-600 text-[10px] rounded-full">
                                                        {app.jobId?.role || "N/A"}
                                                    </span>
                                                </td>
                                                
                                                {/* Offer Letter Column */}
                                                <td className="px-2 py-2 text-center">
                                                    {offerDoc ? (
                                                        <div className="inline-flex items-center gap-2 bg-blue-50/50 px-3 py-1.5 rounded-xl border border-blue-100">
                                                            <div className="flex flex-col items-center">
                                                                <span className="text-[10px] font-bold text-blue-700 uppercase tracking-tight">Offer Signed</span>
                                                                <span className="text-[8px] font-medium text-blue-400 uppercase">{new Date(offerDoc.date).toLocaleDateString()}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1 border-l border-blue-200 pl-2">
                                                                <button onClick={() => { setSelectedDoc(offerDoc); setIsViewerOpen(true); }} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-md transition-all">
                                                                    <FaEye size={12}/>
                                                                </button>
                                                                <button onClick={() => window.open(formatDocUrl(offerDoc.path), "_blank")} className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded-md transition-all">
                                                                    <FaDownload size={12}/>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-[10px] font-medium text-gray-300 uppercase italic">Not Signed</span>
                                                    )}
                                                </td>

                                                {/* Agreements Column */}
                                                <td className="px-2 py-2 text-left">
                                                    <div className="flex flex-col gap-1.5 min-w-[180px]">
                                                        {agreementDocs.length > 0 ? agreementDocs.map((doc) => (
                                                            <div key={doc.id} className="flex items-center justify-between bg-emerald-50/30 p-1.5 rounded-lg border border-emerald-100/50">
                                                                <div className="flex flex-col">
                                                                    <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-tight">
                                                                        {doc.type}
                                                                    </span>
                                                                    <span className="text-[8px] font-medium text-emerald-500 uppercase">{new Date(doc.date).toLocaleDateString()}</span>
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <button onClick={() => { setSelectedDoc(doc); setIsViewerOpen(true); }} className="p-1 text-blue-600 hover:bg-blue-50 rounded-md">
                                                                        <FaEye size={12}/>
                                                                    </button>
                                                                    <button onClick={() => window.open(formatDocUrl(doc.path), "_blank")} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-md">
                                                                        <FaDownload size={12}/>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )) : (
                                                            <span className="text-[10px] font-medium text-gray-300 uppercase italic text-center w-full block">No Agreements</span>
                                                        )}
                                                    </div>
                                                </td>

                                                <td className="px-2 py-2 text-center">
                                                    <div className="flex justify-center">
                                                        <StatusBadge status={docs.length > 0 ? "signed" : "pending"} />
                                                    </div>
                                                </td>
                                                <td className="px-2 py-2 text-center">
                                                    <div className="flex justify-center gap-3">
                                                        {docs.length > 0 && (
                                                            <>
                                                                <button
                                                                    onClick={() => { setSelectedDoc(docs[0]); setIsViewerOpen(true); }}
                                                                    className="px-3 py-1 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-indigo-100 flex items-center justify-center gap-1.5 text-[10px] font-bold"
                                                                >
                                                                    <FaEye className="text-xs" /> View
                                                                </button>
                                                                <button
                                                                    onClick={() => window.open(formatDocUrl(docs[0].path), "_blank")}
                                                                    className="px-3 py-1 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-xl hover:from-indigo-600 hover:to-blue-700 transition-all duration-300 flex items-center justify-center gap-1.5 text-[10px] font-black tracking-widest shadow-md hover:shadow-lg hover:-translate-y-0.5"
                                                                >
                                                                    <FaDownload className="text-xs" /> Save
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        
                            {/* Pagination */}
                            {filteredApps.length > 0 && (
                                <div className="flex flex-col items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 sm:flex-row">
                                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                                        <span>Showing</span>
                                        <span className="font-medium">
                                            {indexOfFirstItem + 1}
                                        </span>
                                        <span>to</span>
                                        <span className="font-medium">
                                            {Math.min(indexOfLastItem, filteredApps.length)}
                                        </span>
                                        <span>of</span>
                                        <span className="font-medium">
                                            {filteredApps.length}
                                        </span>
                                        <span>results</span>

                                        <select
                                            value={pagination.limit}
                                            onChange={(e) => {
                                                const newLimit = Number(e.target.value);
                                                handleItemsPerPageChange(newLimit);
                                            }}
                                            className="p-1 ml-2 text-sm border rounded-lg"
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                        </select>
                                    </div>

                                    <div className="flex items-center gap-2 mt-2 sm:mt-0">
                                        <button
                                            onClick={handlePrevPage}
                                            disabled={pagination.currentPage === 1}
                                            className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                                                pagination.currentPage === 1
                                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                            }`}
                                        >
                                            Previous
                                        </button>

                                        <div className="flex items-center gap-1">
                                            {getPageNumbers().map((page, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => typeof page === 'number' ? handlePageClick(page) : null}
                                                    disabled={page === "..."}
                                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                        page === "..."
                                                            ? "text-gray-500 cursor-default"
                                                            : pagination.currentPage === page
                                                            ? "bg-blue-600 text-white"
                                                            : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    {page}
                                                </button>
                                            ))}
                                        </div>

                                        <button
                                            onClick={handleNextPage}
                                            disabled={pagination.currentPage === pagination.totalPages}
                                            className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                                                pagination.currentPage === pagination.totalPages
                                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                            }`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <FaBriefcase className="text-2xl text-gray-200" />
                            </div>
                            <h2 className="text-lg font-bold text-gray-800">No active records found</h2>
                            <p className="text-xs text-gray-400 mt-1">Try refined search parameters</p>
                        </div>
                    )}
                </div>

                {/* Document Viewer Modal */}
                {isViewerOpen && selectedDoc && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={() => setIsViewerOpen(false)}></div>
                        <div className="relative bg-white w-full h-full max-w-5xl rounded-xl shadow-2xl overflow-hidden flex flex-col border border-gray-200">
                            {/* Modal Header */}
                            <div className="px-6 py-4 bg-white border-b border-gray-200 flex items-center justify-between shrink-0">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shadow-sm">
                                        <FaBriefcase size={18} />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800 tracking-tight uppercase">{selectedDoc.type}</h2>
                                        <p className="text-blue-600 text-[10px] font-bold uppercase tracking-wider mt-0.5">Administration Vault • Signed {new Date(selectedDoc.date).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button 
                                        onClick={() => window.open(formatDocUrl(selectedDoc.path), "_blank")}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-blue-700 transition-all shadow-md"
                                    >
                                        <FaDownload /> Download
                                    </button>
                                    <button onClick={() => setIsViewerOpen(false)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg transition-all">
                                        <FaTimes size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="flex-1 bg-gray-50 p-4 overflow-hidden relative">
                                <iframe 
                                    src={formatDocUrl(selectedDoc.path)} 
                                    className="w-full h-full border border-gray-200 rounded-lg shadow-inner bg-white" 
                                    title="Document Viewer" 
                                />
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default AcceptanceLetter;